// Authenticated fetch helper for the embedded SPA.
//
// Every /api/* call (other than the bootstrap POST to /api/auth/token-exchange)
// needs a fresh `Authorization: Bearer <id_token>` header. id_tokens expire
// in 60 seconds, so there's no point caching — we call `shopify.idToken()`
// before each request and let App Bridge handle the short-lived signing.
//
// Why this wrapper vs. inline fetch:
//   - Single place to handle App Bridge token fetching.
//   - Consistent error shape for callers (never "undefined.something crashes").
//   - Easier to instrument later (metrics, retry on 401 after re-auth, etc).

import type { useAppBridge } from "@shopify/app-bridge-react";

// The App Bridge hook returns a `ShopifyGlobal` object from an internal
// package we don't import directly. Alias it by inference so callers can
// just pass in whatever they got from useAppBridge().
export type Shopify = ReturnType<typeof useAppBridge>;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// Stale-while-revalidate cache for GET responses, keyed by path. Components
// seed their initial state from here (see getCached) so navigating between
// pages is instant: the last response renders immediately while apiFetch
// revalidates in the background.
//
// Backed by sessionStorage so the cache survives a full page reload — App
// Bridge's NavMenu navigations reload the embedded iframe, and without this
// every nav would re-show the bootstrap spinner. All sessionStorage access is
// wrapped in try/catch: a partitioned/blocked third-party iframe can throw, in
// which case we degrade to an in-memory-only cache (the prior behaviour).
//
// Keys are namespaced by SHOP + a schema version (audit F2). Every store's
// admin lives under admin.shopify.com, so the embed iframe's sessionStorage
// SURVIVES an in-tab switch from store A to store B — an unscoped cache
// painted store A's destinations, plan, and planSelectionUrl (deep-linking to
// store A's admin!) under store B. The version tag busts stale pre-migration
// shapes (e.g. a cached /status without planSelectionUrl left the plan
// button dead). v3 (review W31): a cached pre-cap /status has no freePeriod
// and would paint the dashboard for a store whose free period has ended.
// v4 (overlap rework): a cached v3 /api/destinations body has no
// storeContext.overlaps, so the first paint after deploy would show no
// overlap notice until revalidation. One character evicts every dc_cache key,
// via the module-load sweep below.
// Exported for api.test.ts, which must seed sessionStorage under the CURRENT
// prefix: hardcoding a version there made the test silently change meaning on
// a bump (it would start asserting version eviction instead of the
// shouldCache eviction it exists for).
export const CACHE_VERSION = "v4";

/** The shop this embed is rendering for, from Shopify's ?shop= param. */
export const EMBED_SHOP = (() => {
  try {
    return new URLSearchParams(window.location.search).get("shop") ?? "unknown-shop";
  } catch {
    return "unknown-shop";
  }
})();

const CACHE_PREFIX = `dc_cache:${CACHE_VERSION}:${EMBED_SHOP}:`;
const getCache = new Map<string, unknown>();

/** A sessionStorage key scoped to this embed's shop (for non-cache state). */
export function shopScopedKey(base: string): string {
  return `${base}:${EMBED_SHOP}`;
}

// Hydrate the in-memory map from sessionStorage once, at module load — and
// evict any dc_cache entries from other shops/schema versions so a shop
// switch can't resurrect them and storage stays bounded. Entries this build
// no longer caches go too (review S12: Activity rows an older build wrote
// carry shopper emails and phones).
try {
  const stale: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (!key || !key.startsWith("dc_cache:")) continue;
    if (key.startsWith(CACHE_PREFIX) && shouldCache(key.slice(CACHE_PREFIX.length))) {
      const raw = sessionStorage.getItem(key);
      if (raw) getCache.set(key.slice(CACHE_PREFIX.length), JSON.parse(raw));
    } else {
      stale.push(key);
    }
  }
  for (const key of stale) sessionStorage.removeItem(key);
} catch {
  // sessionStorage unavailable — the in-memory cache still works this load.
}

/**
 * Whether a successful response for `path` goes into the SWR cache: GETs
 * only, and never
 *   - an OAuth handshake: the start response carries the OAuth state and the
 *     poll response the grant id, and each 2 s poll added an entry (review
 *     S10);
 *   - a response that names shoppers, which must not sit in sessionStorage
 *     (a customers/redact can't reach it there):
 *       - customer data requests (review W29); a cached "No file yet" badge
 *         would also paint after the download;
 *       - the Activity feed (review S12): each item carries the shopper's
 *         email and phone, its `since` query makes every visit a new key, and
 *         nothing reads that cache back (Activity refetches on mount).
 */
export function shouldCache(path: string, method: string = "GET"): boolean {
  return (
    method.toUpperCase() === "GET" &&
    !path.includes("/oauth/") &&
    !path.startsWith("/api/data-requests") &&
    !path.startsWith("/api/dashboard/activity")
  );
}

/** Last cached response for a GET `path`, or undefined if never fetched. */
export function getCached<T = unknown>(path: string): T | undefined {
  return getCache.get(path) as T | undefined;
}

// Called from components that already have `useAppBridge()` in scope.
// Passing the bridge in rather than calling useAppBridge here keeps this a
// plain non-hook helper (so it can be used inside async callbacks without
// lint warnings).
export async function apiFetch<T = unknown>(
  shopify: Shopify,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const idToken = await shopify.idToken();
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${idToken}`);
  // JSON bodies are the common case; auto-set Content-Type unless caller
  // already specified one (e.g., for a GET with no body).
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(path, { ...init, headers });

  // Try to parse JSON either way — our error responses also come in JSON.
  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) throw toApiError(res, body);
  // Cache successful GET responses for instant stale-while-revalidate reads
  // (in memory + sessionStorage so they survive a NavMenu-triggered reload),
  // minus the paths shouldCache excludes.
  if (shouldCache(path, init.method ?? "GET")) {
    getCache.set(path, body);
    try {
      sessionStorage.setItem(CACHE_PREFIX + path, JSON.stringify(body));
    } catch {
      // storage full / blocked — the in-memory cache is enough.
    }
  }
  return body as T;
}

/** An ApiError from a non-2xx response and its parsed (or raw text) body. */
function toApiError(res: Response, body: unknown): ApiError {
  const rawErr =
    body && typeof body === "object" && body !== null && "error" in body
      ? (body as { error: unknown }).error
      : null;
  // `error` is a plain string on most routes, but the destination /test
  // routes return { message, details }: pull the message out so it doesn't
  // stringify to "[object Object]".
  const msg =
    (typeof rawErr === "string"
      ? rawErr
      : rawErr &&
          typeof rawErr === "object" &&
          "message" in rawErr &&
          typeof (rawErr as { message: unknown }).message === "string"
        ? (rawErr as { message: string }).message
        : rawErr != null
          ? JSON.stringify(rawErr)
          : null) ?? `${res.status} ${res.statusText}`;
  return new ApiError(res.status, msg, body);
}

/** The filename in a Content-Disposition header, or null when there is none. */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const quoted = /filename="([^"]+)"/i.exec(header);
  const bare = quoted ? null : /filename=([^;\s]+)/i.exec(header);
  const name = (quoted?.[1] ?? bare?.[1] ?? "").trim();
  // A name only, never a path.
  return name && !/[\\/]/.test(name) ? name : null;
}

/**
 * Download a file the API builds (review W29: the customer data export).
 * POST with the session token, then hand the body to the browser as a file
 * under the server's filename. Never cached: the body is read as a blob and
 * never touches the SWR cache or sessionStorage. Errors throw ApiError like
 * apiFetch. Returns the filename used.
 */
export async function apiDownload(
  shopify: Shopify,
  path: string,
  fallbackName: string,
  init: RequestInit = {},
): Promise<string> {
  const idToken = await shopify.idToken();
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${idToken}`);
  const res = await fetch(path, { method: "POST", cache: "no-store", ...init, headers });
  if (!res.ok) {
    const text = await res.text();
    let body: unknown = text || null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      // not JSON: keep the text
    }
    throw toApiError(res, body);
  }
  const blob = await res.blob();
  const filename = filenameFromDisposition(res.headers.get("Content-Disposition")) ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke later: some browsers read the blob after click() returns.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return filename;
}
